import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clearAuthSession, isUserSignedIn } from "../services/auth/auth-session";
import LoginDialog from "../views/auth/login-dialog";
import ProfileCompleteDialog, { isProfileComplete } from "./ProfileCompleteDialog";
import { fetchMyProfile } from "../services/auth/profile-api";
import { fetchMySubscription } from "../services/api/subscriptions";
import { getMyLeagues } from "../services/api/leagues";
import { canUserSubscribe, allowlistActive } from "../config/pricing-access";

const NAV_ITEMS = [
  { label: "Home", path: "/" },
  { label: "Find Hockey", path: "/search" },
  { label: "My Schedule", path: "/my-schedule", requireSignedIn: true },
  { label: "My Leagues", path: "/my-leagues", requireLeagues: true },
  { label: "Pricing", path: "/pricing", requirePricing: true },
];

export default function TopNavBar({ mode, onToggleTheme }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [profileCompleteOpen, setProfileCompleteOpen] = useState(false);
  const [profileCompleteData, setProfileCompleteData] = useState(null);
  const [signedIn, setSignedIn] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [hasSubscription, setHasSubscription] = useState(false);
  // Whether the person belongs to any league. Players need My Leagues too (to archive leagues), not only organizers.
  const [hasLeagues, setHasLeagues] = useState(false);

  const mobileMenuOpen = useMemo(() => Boolean(menuAnchor), [menuAnchor]);
  const profileMenuOpen = useMemo(() => Boolean(profileMenuAnchor), [profileMenuAnchor]);
  const userInitial = useMemo(() => (userEmail ? userEmail[0].toUpperCase() : "?"), [userEmail]);
  const showMyLeagues = hasSubscription || hasLeagues;

  useEffect(() => {
    let mounted = true;
    isUserSignedIn({ verifyWithIntrospection: true }).then((active) => {
      if (!mounted) {
        return;
      }

      setSignedIn(active);
      if (!active) {
        setUserEmail("");
        setHasSubscription(false);
        return;
      }

      Promise.allSettled([fetchMyProfile(), fetchMySubscription()])
        .then(([profileResult, subResult]) => {
          if (!mounted) return;
          const profile = profileResult.status === "fulfilled" ? profileResult.value : null;
          const subscription = subResult.status === "fulfilled" ? subResult.value : null;
          setUserEmail(profile?.email || "");
          setHasSubscription(!!subscription);
        });
    });

    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  // People without a subscription (players) only get My Leagues once they belong to a league. Keep checking as they
  // move around until they do, since signing up for a game is what makes that true; organizers skip this.
  useEffect(() => {
    if (!signedIn) {
      setHasLeagues(false);
      return undefined;
    }
    if (hasSubscription || hasLeagues) return undefined;
    let mounted = true;
    getMyLeagues()
      .then((list) => {
        if (mounted) setHasLeagues(Array.isArray(list) && list.length > 0);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [signedIn, hasSubscription, hasLeagues, location.pathname]);

  // Re-check auth when GoogleAutoRefresh silently refreshes the session
  useEffect(() => {
    const handler = () => {
      isUserSignedIn({ verifyWithIntrospection: false }).then((active) => {
        setSignedIn(active);
        if (!active) return;
        Promise.allSettled([fetchMyProfile(), fetchMySubscription()])
          .then(([profileResult, subResult]) => {
            const profile = profileResult.status === "fulfilled" ? profileResult.value : null;
            const subscription = subResult.status === "fulfilled" ? subResult.value : null;
            setUserEmail(profile?.email || "");
            setHasSubscription(!!subscription);
          });
      });
    };
    window.addEventListener("puck-pickup-auth-change", handler);
    return () => window.removeEventListener("puck-pickup-auth-change", handler);
  }, []);

  const closeMobileMenu = () => {
    setMenuAnchor(null);
  };

  const closeProfileMenu = () => {
    setProfileMenuAnchor(null);
  };

  const handleNavigate = (path) => {
    closeMobileMenu();
    closeProfileMenu();
    navigate(path);
  };

  const handleSignIn = () => {
    closeMobileMenu();
    setLoginOpen(true);
  };

  const handleLoginSuccess = () => {
    isUserSignedIn({ verifyWithIntrospection: false }).then((active) => {
      setSignedIn(active);
      if (active) {
        Promise.allSettled([fetchMyProfile(), fetchMySubscription()])
          .then(([profileResult, subResult]) => {
            const profile = profileResult.status === "fulfilled" ? profileResult.value : null;
            const subscription = subResult.status === "fulfilled" ? subResult.value : null;
            setUserEmail(profile?.email || "");
            setHasSubscription(!!subscription);

            if (profile && !isProfileComplete(profile)) {
              setProfileCompleteData(profile);
              setProfileCompleteOpen(true);
            }

            window.dispatchEvent(new Event("puck-pickup-auth-change"));
          });
      }
    });
  };

  const handleProfileCompleteClose = ({ updated }) => {
    setProfileCompleteOpen(false);
    if (updated) {
      fetchMyProfile().then((profile) => {
        if (profile) setUserEmail(profile.email || "");
      });
    }
  };

  const handleSignOut = () => {
    closeMobileMenu();
    closeProfileMenu();
    clearAuthSession();
    setSignedIn(false);
    setUserEmail("");
    setHasSubscription(false);
    window.dispatchEvent(new Event("puck-pickup-auth-change"));
    navigate("/");
  };

  return (
    <>
    <LoginDialog
      open={loginOpen}
      onClose={() => setLoginOpen(false)}
      onSuccess={handleLoginSuccess}
    />
    <ProfileCompleteDialog
      open={profileCompleteOpen}
      onClose={handleProfileCompleteClose}
      profile={profileCompleteData}
    />
    <AppBar position="sticky">
      <Toolbar>
        <Typography
          variant="h6"
          component="button"
          onClick={() => handleNavigate("/")}
          sx={{
            color: "inherit",
            border: 0,
            background: "none",
            cursor: "pointer",
            fontSize: "1.5rem",
            fontWeight: 600,
          }}
        >
          Puck Pickup
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
          {NAV_ITEMS.filter((item) => {
            if (item.requireSignedIn) return signedIn;
            if (item.requireLeagues) return showMyLeagues;
            if (item.requirePricing) return !allowlistActive || canUserSubscribe(userEmail);
            return true;
          }).map((item) => (
            <Button
              key={item.path}
              color="inherit"
              onClick={() => handleNavigate(item.path)}
              sx={{ textTransform: "none" }}
            >
              {item.label}
            </Button>
          ))}

          <FormControlLabel
            sx={{ ml: 1, mr: 1 }}
            control={<Switch checked={mode === "dark"} onChange={onToggleTheme} color="default" />}
            label={mode === "dark" ? "Dark" : "Light"}
          />

          {!signedIn ? (
            <Button variant="outlined" color="inherit" onClick={handleSignIn} sx={{ textTransform: "none" }}>
              Login
            </Button>
          ) : (
            <Tooltip title={userEmail}>
              <IconButton color="inherit" onClick={(event) => setProfileMenuAnchor(event.currentTarget)}>
                <Avatar sx={{ width: 32, height: 32, fontSize: "0.95rem" }}>{userInitial}</Avatar>
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: { xs: "flex", md: "none" } }}>
          <IconButton
            size="large"
            color="inherit"
            aria-label="open navigation menu"
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            <MenuIcon />
          </IconButton>
          <Menu anchorEl={menuAnchor} open={mobileMenuOpen} onClose={closeMobileMenu}>
            {NAV_ITEMS.filter((item) => {
              if (item.requireSignedIn) return signedIn;
              if (item.requireLeagues) return showMyLeagues;
              if (item.requirePricing) return !allowlistActive || canUserSubscribe(userEmail);
              return true;
            }).map((item) => (
              <MenuItem key={item.path} onClick={() => handleNavigate(item.path)}>
                {item.label}
              </MenuItem>
            ))}
            <MenuItem sx={{ gap: 1 }}>
              <FormControlLabel
                control={<Switch checked={mode === "dark"} onChange={onToggleTheme} color="default" />}
                label={mode === "dark" ? "Dark" : "Light"}
                sx={{ m: 0 }}
              />
            </MenuItem>
            {!signedIn ? (
              <MenuItem onClick={handleSignIn}>Login</MenuItem>
            ) : (
              <MenuItem onClick={(event) => setProfileMenuAnchor(event.currentTarget)}>Profile</MenuItem>
            )}
          </Menu>
        </Box>

        {signedIn ? (
          <Menu anchorEl={profileMenuAnchor} open={profileMenuOpen} onClose={closeProfileMenu}>
            <MenuItem disabled sx={{ opacity: 1 }}>
              <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                {userEmail}
              </Typography>
            </MenuItem>
            <Divider />
            {hasSubscription && (
              <MenuItem onClick={() => handleNavigate("/my-subscription")}>My Subscription</MenuItem>
            )}
            <MenuItem onClick={() => handleNavigate("/profile")}>Edit profile</MenuItem>
            <MenuItem onClick={handleSignOut}>Logout</MenuItem>
          </Menu>
        ) : null}
      </Toolbar>
    </AppBar>
    </>
  );
}
